import React from "react";
import { useLoaderData } from "react-router-dom";
import { getListingsMap } from "../../../data/admin/listingMap";

const FALLBACK_POINTS = [
    { label: "Paris", lat: 48.8566, lon: 2.3522, count: 128 },
    { label: "Lille", lat: 50.6292, lon: 3.0573, count: 42 },
    { label: "Strasbourg", lat: 48.5734, lon: 7.7521, count: 31 },
    { label: "Nantes", lat: 47.2184, lon: -1.5536, count: 37 },
    { label: "Bordeaux", lat: 44.8378, lon: -0.5792, count: 54 },
    { label: "Lyon", lat: 45.764, lon: 4.8357, count: 73 },
    { label: "Toulouse", lat: 43.6047, lon: 1.4442, count: 49 },
    { label: "Marseille", lat: 43.2965, lon: 5.3698, count: 61 }
];


function normalizeMapData(raw) {
    if (Array.isArray(raw)) {
        return { entries: raw, generatedAt: null, mapboxToken: "" };
    }
    const entries = raw?.entries ?? raw?.items ?? [];
    return { entries, generatedAt: raw?.generatedAt ?? null, mapboxToken: raw?.mapboxToken ?? "" };
}

function formatDateTime(value) {
    if (!value) {
        return null;
    }
    return new Date(value).toLocaleString("fr-FR");
}

export async function loader() {
    try {
        const data = await getListingsMap();
        return normalizeMapData(data);
    } catch (error) {
        if (error?.status === 404) {
            return normalizeMapData([]);
        }
        throw error;
    }
}

export default function AdminListingMapPage() {
    const { entries, generatedAt, mapboxToken } = useLoaderData();
    const mapPoints = entries?.length ? entries : FALLBACK_POINTS;
    const lastUpdate = formatDateTime(generatedAt) ?? "Données de démonstration";
    const mapToken = mapboxToken;

    return (
        <div className="admin-map-page">
            <div className="admin-map-header">
                <h1>Répartition des annonces en France</h1>
                <p className="admin-map-description">
                    Cette carte affiche la densité des annonces actives par zone géographique.
                </p>
                <p className="admin-map-generated">Mise à jour : {lastUpdate}</p>
            </div>

            <div className="admin-map-grid">
                <section className="admin-map-card admin-map-card-main">
                    <div className="admin-map-wrapper">
                        {!mapToken && (
                            <div className="admin-map-empty">
                                <strong>Clé Mapbox manquante.</strong>
                                <span>Renseignez Mapbox:AccessToken dans appsettings.</span>
                            </div>
                        )}
                        {mapToken && (
                            <iframe
                                className="admin-map-iframe"
                                title="Carte Mapbox France"
                                src="/admin-map.html"
                                loading="lazy"
                            />
                        )}
                    </div>
                    <div className="admin-map-legend">
                        <span className="admin-map-legend-title">Légende</span>
                        <div className="admin-map-legend-items">
                            <div className="admin-map-legend-item">
                                <span className="admin-map-legend-dot small" />
                                <span>Faible densité</span>
                            </div>
                            <div className="admin-map-legend-item">
                                <span className="admin-map-legend-dot medium" />
                                <span>Densité moyenne</span>
                            </div>
                            <div className="admin-map-legend-item">
                                <span className="admin-map-legend-dot large" />
                                <span>Forte densité</span>
                            </div>
                        </div>
                    </div>
                </section>

                <aside className="admin-map-card">
                    <h2>Zones les plus actives</h2>
                    <ul className="admin-map-list">
                        {mapPoints
                            .slice()
                            .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
                            .slice(0, 6)
                            .map((point) => (
                                <li key={point.label} className="admin-map-list-item">
                                    <span className="admin-map-list-label">{point.label}</span>
                                    <span className="admin-map-list-count">{(point.count ?? 0).toLocaleString("fr-FR")} annonces</span>
                                </li>
                            ))}
                    </ul>
                    <div className="admin-map-footnote">
                        Les points représentent des regroupements par zone. Connectez-vous à l&apos;API pour charger les données réelles.
                    </div>
                </aside>
            </div>
        </div>
    );
}
